import { useRef, useState } from "react";
import { api } from "../api";
import { GiveUp } from "../components/GiveUp";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { haptic } from "../telegram";
import { useGame } from "../useGame";

interface View {
  base: string;
  minLen: number;
  maxLen: number;
  /** index 0 = length 3 … 4 = length 7 */
  slots: (string | null)[];
  misses: number;
  guesses: number;
  /** After finish: the most common answer per still-empty slot. */
  samples?: (string | null)[];
}

interface GuessExtra {
  accepted: boolean;
  why?: "letters" | "slot_full";
  len?: number;
}

const COPY = {
  bad_word: "Тільки українські літери",
  bad_length: "Треба слово на 3-7 літер",
  not_in_dictionary: "Не знаю такого іменника",
  already_guessed: "Це слово вже стоїть",
};

export function Slova({ date }: { date?: string | undefined }) {
  const g = useGame<View>("slova", COPY, date);
  const [word, setWord] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";

  const submit = async () => {
    const w = word.trim();
    if (!w || finished || g.busy) return;
    inputRef.current?.focus();
    const next = await g.run(() => api.guess<View>("slova", w, date));
    if (next) {
      const extra = next.extra as GuessExtra | undefined;
      if (extra?.accepted) {
        setWord("");
      } else if (extra?.why === "letters") {
        haptic.error();
        g.showToast("Таких літер немає в слові дня");
      } else if (extra?.why === "slot_full") {
        haptic.error();
        g.showToast(`На ${extra.len} літер слово вже є`);
      }
    }
  };

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const won = s.status === "won";

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="СЛОВА ЗІ СЛОВА" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <div className="flex-1 overflow-y-auto px-4 pb-2">
        <div className="mx-auto mt-1 max-w-[420px] rounded-2xl bg-tgsection px-4 py-4 text-center">
          <div className="text-[11px] uppercase tracking-wide text-tghint">слово дня</div>
          <div className="mt-1 break-all text-2xl font-extrabold tracking-[0.15em]">{v.base.toUpperCase()}</div>
        </div>
        <div className="mt-1.5 text-center text-xs text-tghint">
          склади іменник на кожну довжину з цих літер · спроб: {v.guesses}
          {v.misses > 0 ? ` · промахів: ${v.misses}` : ""}
        </div>

        <ul className="mx-auto mt-3 max-w-[420px] space-y-1.5">
          {v.slots.map((slot, i) => {
            const len = v.minLen + i;
            const sample = v.samples?.[i];
            return (
              <li
                key={len}
                className={`flex h-[46px] items-center gap-3 rounded-xl px-3 ${slot ? "bg-ok/15" : "bg-tgsection"}`}
              >
                <span className="w-14 shrink-0 text-xs font-semibold text-tghint">{len} літер</span>
                {slot ? (
                  <span className="text-[15px] font-bold tracking-wide">{slot.toUpperCase()} ✓</span>
                ) : finished && sample ? (
                  <span className="text-[15px] text-tghint">
                    було, наприклад: <b className="text-tgtext">{sample.toUpperCase()}</b>
                  </span>
                ) : (
                  <span className="flex gap-1">
                    {Array.from({ length: len }, (_, j) => (
                      <span key={j} className="h-[10px] w-[10px] rounded-[3px] bg-[rgba(128,128,128,0.35)]" />
                    ))}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <div style={{ paddingBottom: "max(var(--tg-safe-area-inset-bottom, 0px), 10px)" }}>
        <form
          className="flex gap-2 px-4 pb-1"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <input
            ref={inputRef}
            value={word}
            onChange={(e) => setWord(e.target.value)}
            readOnly={finished}
            placeholder={finished ? "гра закінчена" : "іменник з цих літер"}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            enterKeyHint="send"
            className="h-[44px] flex-1 rounded-xl border border-[rgba(128,128,128,0.35)] bg-transparent px-4 text-[16px] text-tgtext outline-none placeholder:text-[rgba(128,128,128,0.7)] focus:border-tgbutton disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={finished || g.busy || !word.trim()}
            onMouseDown={(e) => e.preventDefault()}
            className="h-[44px] rounded-xl bg-tgbutton px-5 text-[15px] font-bold text-tgbuttontext active:brightness-90 disabled:opacity-35"
          >
            {g.busy ? "…" : "→"}
          </button>
        </form>
        {!finished && (
          <div className="flex px-4">
            <GiveUp busy={g.busy} onGiveUp={() => void g.run(() => api.giveUp<View>("slova", date))} />
          </div>
        )}
      </div>

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={won ? "Словотворець!" : "Здався"}
          subtitle={
            won
              ? `за ${v.guesses}${v.misses ? ` (${v.misses} промахів)` : ""} · стрік ${s.me.streak} 🔥`
              : `знайшов ${v.slots.filter(Boolean).length}/5 · стрік згорів`
          }
          scoreLabel="avg спроб"
          formatScore={(n) => `${n}`}
          onDismiss={() => g.setSheetOpen(false)}
        />
      )}
    </Shell>
  );
}
